import { PAGE_KEY } from './constants';

export const en = {
  [PAGE_KEY]: {
    // Mini header (task-06)
    MINI_HEADER_BACK_LABEL: 'Back to concepts list',

    // Meta row (task-06)
    META_CODE_LABEL: 'Code',
    META_TYPE_LABEL: 'Type',
    META_CFDI_LABEL: 'CFDI',
    META_RULE_TYPE_LABEL: 'Current rule type',

    // Empty states (task-06)
    EMPTY_CONCEPT_NOT_FOUND_TITLE: "We couldn't find this concept",
    EMPTY_CONCEPT_NOT_FOUND_DESCRIPTION:
      "The code in the URL doesn't match any concept in your organization's catalog.",
    EMPTY_CONCEPT_NOT_FOUND_ACTION: 'Back to list',

    // No-access gate state — added by task-06 per the orchestrator's "Gate de la
    // ruta" correction; this is a visibility gate, not a server-verified
    // permission (the authority is the save mutation's UserPermissionErrorResponse,
    // Task 13), so the copy never claims the permission was checked server-side.
    EMPTY_NO_ACCESS_TITLE: "You don't have access to this screen",
    EMPTY_NO_ACCESS_DESCRIPTION:
      "The formula editor is only available to Crehana's support team.",

    // Error state (task-06)
    ERROR_TITLE: "We couldn't load the concept",
    ERROR_RETRY_BUTTON: 'Retry',

    // Discard dialog (task-07) — the discard-on-cancel flow survives.
    RULES_DISCARD_DIALOG_TITLE: 'Discard unsaved changes?',
    RULES_DISCARD_DIALOG_BODY:
      "You're about to switch rules and lose what you wrote in this expression.",
    RULES_DISCARD_DIALOG_CONFIRM: 'Discard and continue',

    // AST editor + echo (task-09)
    AST_TEXTAREA_LABEL: 'Formula expression (AST)',
    AST_TEXTAREA_PLACEHOLDER:
      'Write the formula here in JSON, or insert a reference from the panel.',
    AST_FORMAT_BUTTON: 'Format',
    AST_METRIC_NODES: '{{count}} nodes',
    AST_METRIC_DEPTH: 'depth {{depth}}/12',
    AST_METRIC_DEPENDENCIES: '{{count}} dependencies',
    ECHO_RAW_FALLBACK_HINT:
      "This expression doesn't have the expected shape yet — showing the raw JSON instead.",
    // Defect 3 (post-review): own copy over `JSON.parse`'s message, keeping
    // the position (the only useful clue to locate the error in a long JSON).
    AST_JSON_PARSE_ERROR:
      'Invalid JSON — check the character near position {{position}}.',
    // The engine doesn't always report a position (e.g. "Unexpected end of
    // JSON input" for truncated JSON) — never leak the raw English message
    // in that case.
    AST_JSON_PARSE_ERROR_GENERIC: 'Invalid JSON — check the expression syntax.',

    // Validation panel (task-10, -9 keys in task-08 — AC-8: only rows with
    // findings render, so no "all clear" copy has a consumer anymore).
    VALIDATION_SYNTAX_TITLE: 'Syntax',
    VALIDATION_SYNTAX_ERR_HINT: 'Syntax error at {{path}}',
    VALIDATION_INCOMPLETE_TITLE: 'Incomplete formula',
    // AC-7: distinct from Syntax — the JSON is valid, some data is just missing.
    VALIDATION_INCOMPLETE_HINT: '{{count}} values are missing',
    VALIDATION_REFERENCES_TITLE: 'References',
    VALIDATION_REFERENCES_WARN_HINT:
      "There's a valid reference that can't be calculated yet",
    VALIDATION_REFERENCES_ERR_HINT:
      "There's a reference that doesn't exist or is misspelled",
    VALIDATION_DEPENDENCIES_TITLE: 'Dependencies',
    // AC-6: a concept that ends up depending on itself.
    VALIDATION_DEPENDENCIES_ERR_HINT:
      'This formula creates a circular dependency between concepts',
    VALIDATION_DEPTH_TITLE: 'Nesting',
    VALIDATION_DEPTH_WARN_HINT:
      'Getting close to the nesting limit ({{depth}}/12)',
    VALIDATION_DEPTH_ERR_HINT: 'Exceeds the nesting limit ({{depth}}/12)',
    VALIDATION_PCT_TITLE: 'Data source',
    // AC-8: the backend accepts this formula, so it's a warning, not a blocker.
    VALIDATION_PCT_WARN_HINT:
      'A "_pct" field is not being divided by 100 — the result could come out 100x too large',

    // Reference panel (task-11)
    REF_PANEL_TITLE: 'Insert reference',
    REF_PANEL_SEARCH_PLACEHOLDER: 'Search by field name…',
    REF_PANEL_GROUP_CONTRACT: 'Contract',
    REF_PANEL_GROUP_RUN: 'Payroll run',
    REF_PANEL_GROUP_ACCUMULATOR: 'Accumulator',
    REF_PANEL_GROUP_FISCAL: 'Fiscal',
    REF_PANEL_GROUP_INCIDENCE: 'Incidence',
    REF_PANEL_GROUP_CONCEPT: 'Concept',
    // AC-9: integration_factor is the only disabled field — it's in the DSL
    // but the port doesn't serve it (UNSERVED_CONTRACT_REF_FIELDS).
    REF_UNSERVED_TOOLTIP:
      "This field exists in the contract, but it isn't available for formulas yet — using it will make saving fail.",
    // B-4: ref.incidence's subtype is always "all", never editable.
    REF_INCIDENCE_SUBTYPE_TOOLTIP:
      'The subtype is always "all": incidence capture is free text, so pinning a specific value would silently stop matching on any spelling variation.',

    // ref.contract — 12 fields (CONTRACT_REF_FIELDS)
    REF_CONTRACT_DAILY_WAGE: 'Daily wage',
    REF_CONTRACT_BASE_SALARY: 'Base salary',
    REF_CONTRACT_INTEGRATION_FACTOR: 'Integration factor',
    REF_CONTRACT_WEEKLY_HOURS: 'Weekly hours',
    REF_CONTRACT_START_DATE: 'Start date',
    REF_CONTRACT_STATE_CODE: 'State',
    REF_CONTRACT_VACATION_DAYS: 'Vacation days',
    REF_CONTRACT_CHRISTMAS_BONUS_DAYS: 'Christmas bonus days',
    REF_CONTRACT_VACATION_PREMIUM_PCT: 'Vacation premium',
    REF_CONTRACT_SENIORITY_YEARS: 'Years of seniority',
    REF_CONTRACT_PAY_FREQUENCY: 'Pay frequency',
    REF_CONTRACT_IMSS_SALARY_TYPE: 'IMSS salary type',

    // ref.run — 7 fields (RUN_REF_FIELDS)
    REF_RUN_DAYS_WORKED: 'Days worked',
    REF_RUN_DAYS_PAID: 'Days paid',
    REF_RUN_PERIOD_DAYS: 'Period days',
    REF_RUN_PERIOD_START: 'Period start',
    REF_RUN_PERIOD_END: 'Period end',
    REF_RUN_PAYMENT_DATE: 'Payment date',
    REF_RUN_DAYS_BASE: 'Base days',

    // ref.accumulator — 4 fields (ACCUMULATOR_REF_FIELDS)
    REF_ACCUMULATOR_TAXABLE_INCOME_YTD: 'Taxable income (YTD)',
    REF_ACCUMULATOR_ISR_WITHHELD_YTD: 'ISR withheld (YTD)',
    REF_ACCUMULATOR_SUBSIDY_APPLIED_YTD: 'Subsidy applied (YTD)',
    REF_ACCUMULATOR_GROSS_INCOME_YTD: 'Gross income (YTD)',

    // ref.fiscal — 5 tables (FISCAL_REF_TABLES); the field has no catalog (B-5)
    REF_FISCAL_PARAMETERS: 'Parameters',
    REF_FISCAL_ISR: 'ISR',
    REF_FISCAL_EMPLOYMENT_SUBSIDY: 'Employment subsidy',
    REF_FISCAL_IMSS_RATE: 'IMSS rate',
    REF_FISCAL_ISN_RATE: 'ISN rate',

    // ref.incidence — 9 types (INCIDENCE_REF_TYPES); the 8 fields render
    // untranslated (same precedent as `ruleType`).
    REF_INCIDENCE_ABSENTEEISM: 'Absenteeism',
    REF_INCIDENCE_DISABILITY: 'Disability',
    REF_INCIDENCE_OVERTIME: 'Overtime',
    REF_INCIDENCE_SAVINGS: 'Savings',
    REF_INCIDENCE_INFONAVIT: 'Infonavit',
    REF_INCIDENCE_FONACOT: 'Fonacot',
    REF_INCIDENCE_PENSION: 'Pension',
    REF_INCIDENCE_RECURRING: 'Recurring',
    REF_INCIDENCE_OTHER: 'Other',

    // Defect 2 (post-review): `example` in `concept-ast-catalog.ts` is a
    // format hint, never a resolved value from a real employee (D-5) — this
    // prefix keeps it from reading like a live read.
    REF_EXAMPLE_VALUE: 'e.g. {{value}}',

    // Units (TAstReferenceFieldUnit) — token → human copy
    UNIT_CURRENCY: 'currency',
    UNIT_PERCENT: '%',
    UNIT_DAYS: 'days',
    UNIT_HOURS: 'hours',
    UNIT_DATE: 'date',
    UNIT_CATALOG: 'catalog',
    UNIT_FACTOR: 'factor',
    UNIT_COUNT: 'count',

    // Read-only view for non-FORMULA rules (task-12, AC-11)
    READONLY_TITLE: "This rule can't be edited here",
    READONLY_FIXED_DESCRIPTION:
      "It's a fixed-amount rule. Its value is shown for reference only.",
    READONLY_MANUAL_INPUT_DESCRIPTION:
      "It's a manual-entry rule: there's no expression to show.",
    READONLY_TABLE_LOOKUP_DESCRIPTION:
      "It's a table-lookup rule. Its expression is shown for reference only.",
    READONLY_NO_EDIT_HINT:
      'The formula editor only supports FORMULA-type rules.',

    // Save / footer / history table (task-13)
    BUTTON_CANCEL: 'Cancel',
    BUTTON_SAVE: 'Save rule',
    TOAST_SAVED: 'Rule saved',
    // AC-2 · AC-6: only a title — the body is the backend's own `message`,
    // rendered verbatim, never rewritten.
    VALIDATION_BACKEND_TITLE: "We couldn't save this rule",
    RULES_HISTORY_TABLE_TITLE: 'Rule history',
    RULES_HISTORY_COLUMN_RULE_TYPE: 'Type',
    RULES_HISTORY_COLUMN_PRIORITY: 'Priority',
    RULES_HISTORY_COLUMN_VALID_FROM: 'Valid from',
    RULES_HISTORY_COLUMN_VALID_TO: 'Valid until',
    // Not in the task's literal key list — covers the network-exception /
    // malformed-response edge the backend's own error unions don't carry a
    // `message` for (see `use-save-concept-rule.ts`'s `isExpressionInput`
    // guard and its `catch` block).
    SAVE_UNEXPECTED_ERROR: "We couldn't save this rule. Please try again.",

    // task-03 — AC-2 grammar
    // Named-function operations (AC-2)
    OP_MAX: 'max',
    OP_MIN: 'min',
    OP_ROUND: 'round',
    OP_ABS: 'absolute value',
    OP_FLOOR: 'floor',
    OP_CEIL: 'ceiling',
    OP_ISR_LOOKUP: 'ISR table',
    OP_SUBSIDY_LOOKUP: 'employment subsidy table',
    OP_AND: 'and',
    OP_OR: 'or',
    OP_NOT: 'not',
    // Special forms
    OP_IF: 'if {{condition}} → {{then}} ; else → {{else}}',
    OP_GET: '{{target}} → {{field}}',
    OP_DEPENDS_ON: 'depends on: {{list}}',
    // `get(...)` field names
    GET_FIELD_RETENCION: 'withholding',
    GET_FIELD_SUBSIDIO: 'subsidy',
    // ref.fiscal — additive allowlist, NOT a validation catalog (B-10)
    REF_FISCAL_FIELD_UMA_DAILY: 'Daily UMA',
    REF_FISCAL_FIELD_EM_EXCEDENTE_3UMA: 'Excess over 3 UMA',
    REF_FISCAL_FIELD_EM_PRESTACIONES_DINERO: 'Cash benefits',
    REF_FISCAL_FIELD_EM_GASTOS_MEDICOS_PENSIONADOS:
      'Medical expenses for pensioners',
    REF_FISCAL_FIELD_IV_PRINCIPAL: 'Disability and life',
    REF_FISCAL_FIELD_RCV_CESANTIA_VEJEZ: 'Retirement, severance and old age',
    // ref.incidence fields — closed catalog (B-9)
    REF_INCIDENCE_FIELD_DAYS: 'Days',
    REF_INCIDENCE_FIELD_TOTAL_HOURS: 'Total hours',
    REF_INCIDENCE_FIELD_HOURS_FACTOR_50: 'Hours at 50%',
    REF_INCIDENCE_FIELD_HOURS_FACTOR_100: 'Hours at 100%',
    REF_INCIDENCE_FIELD_HOURS_FACTOR_200: 'Hours at 200%',
    REF_INCIDENCE_FIELD_AMOUNT: 'Amount',
    REF_INCIDENCE_FIELD_PERCENTAGE: 'Percentage',
    REF_INCIDENCE_FIELD_COUNT: 'Count',

    // task-04 — AC-4 sources table
    SOURCES_TITLE: 'Where each value comes from',
    SOURCES_COLUMN_DATA: 'Data',
    SOURCES_COLUMN_ORIGIN: 'Origin',
    SOURCES_COLUMN_PATH: 'Path',
    SOURCE_CONCEPT_NO_RULE_TAG: 'No rule',
    SOURCE_CONCEPT_NO_RULE_HINT:
      "This concept has no rule loaded — it can't be calculated.",
    SOURCE_ORIGIN_CONTRACT: 'Contract',
    SOURCE_ORIGIN_RUN: 'Payroll run',
    SOURCE_ORIGIN_FISCAL: 'Fiscal table',
    SOURCE_ORIGIN_CONCEPT: 'Another concept',
    SOURCE_ORIGIN_ACCUMULATOR: 'Accumulator',
    SOURCE_ORIGIN_INCIDENCE: 'Incidence',

    // task-05 — AC-2/AC-5 reading card
    READING_TITLE: 'How it is calculated',
    READING_CURRENT_RULE_TAG: 'In effect since {{validFrom}}',
    // task-08 (post-task-07 defect fix): replaces the `EMPTY_NO_RULE_*` /
    // `EMPTY_NO_CURRENT_RULE_*` keys the task-07-deleted full-page
    // `EmptyState` left orphaned — the editor stays reachable, so the call
    // to action lives here, not behind a page-level block.
    READING_NO_FORMULA:
      "This concept doesn't have a formula yet. Write one to create its first rule.",

    // task-06 — AC-7 disclosure
    AST_DISCLOSURE_SHOW: 'View JSON',
    AST_DISCLOSURE_HIDE: 'Hide JSON',
    AST_DISCLOSURE_FINDINGS_BADGE: '{{count}} warning',
    AST_DISCLOSURE_FINDINGS_BADGE_plural: '{{count}} warnings',

    // task-07 — AC-6 save sequence, AC-5 history marker
    SAVE_ANNOUNCE_TITLE: 'Before you save',
    SAVE_ANNOUNCE_BODY:
      'The new version will apply starting today ({{today}}), and the previous one will close on {{yesterday}}.',
    SAVE_ANNOUNCE_FIRST_BODY:
      "This will be the concept's first rule, in effect starting today ({{today}}).",
    SAVE_HISTORY_NOT_CLOSED_TITLE:
      'The new rule already calculates, but the history is incomplete',
    // Deliberately does NOT say "save again to fix it": saving again
    // creates a third rule and only closes the second one, leaving the
    // original open forever (memory.md Q-4) — that isn't a real fix.
    SAVE_HISTORY_NOT_CLOSED_BODY:
      "We couldn't close the previous rule. The calculation already uses the new version, but the previous one is still open in the history.",
    RULES_HISTORY_CURRENT_TAG: 'Current',
  },
};
